import { useState } from 'react';
import { useForm, useController, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Field, FieldError, FieldLabel } from '@/shared/components/ui/field';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldSelect } from '@/shared/components/custom/form-field/FormFieldSelect';
import { ColorPickerPopover } from '@/shared/components/custom/color-picker/color-picker.index';
import { getPaletteColor } from '@/core/lib/colors';
import { statusPopoverSchema, tagPopoverSchema } from '../schemas';
import type { StatusPopoverFormValues, TagPopoverFormValues } from '../schemas';

type NameColorFormValues = StatusPopoverFormValues | TagPopoverFormValues;

const STATUS_DEFAULT_VALUES: StatusPopoverFormValues = { name: '', color: '', type: 'Todo' };
const TAG_DEFAULT_VALUES: TagPopoverFormValues = { name: '', color: '' };

interface NameColorPopoverFormProps {
  kind: 'status' | 'tag';
  title: string;
  nameLabel: string;
  addLabel: string;
  addAriaLabel: string;
  onSubmit: (values: NameColorFormValues) => void;
  isPending?: boolean;
  testIdPrefix: string;
  /** Only rendered when `kind === 'status'` — label + options for the status `type` select. */
  typeLabel?: string;
  typeOptions?: Array<{ value: string; label: string }>;
}

/** Shared "name + color" popover form used by StatusPopoverForm and TagPopoverForm. */
export function NameColorPopoverForm({
  kind,
  title,
  nameLabel,
  addLabel,
  addAriaLabel,
  onSubmit,
  isPending = false,
  testIdPrefix,
  typeLabel,
  typeOptions,
}: NameColorPopoverFormProps) {
  const [open, setOpen] = useState(false);
  const schema = kind === 'status' ? statusPopoverSchema : tagPopoverSchema;
  const defaultValues = kind === 'status' ? STATUS_DEFAULT_VALUES : TAG_DEFAULT_VALUES;

  const { control, handleSubmit, reset } = useForm<NameColorFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) reset(defaultValues);
  }

  function handleFormSubmit(values: NameColorFormValues) {
    onSubmit(values);
    handleOpenChange(false);
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="ml-1 size-7 shrink-0"
              aria-label={addAriaLabel}
              data-testid={`${testIdPrefix}-add-button`}
            >
              <Plus className="size-3.5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{addAriaLabel}</TooltipContent>
      </Tooltip>

      <PopoverContent className="w-64 p-3" align="start" data-testid={`${testIdPrefix}-popover`}>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-3">
          <p className="text-sm font-medium">{title}</p>

          <div className="flex items-end gap-2">
            <FormFieldInput
              control={control}
              name="name"
              label={nameLabel}
              floatingLabel
              disabled={isPending}
              data-testid={`${testIdPrefix}-name-input`}
            />
            <ColorSwatchField control={control} testIdPrefix={testIdPrefix} isPending={isPending} />
          </div>

          {kind === 'status' && typeOptions && typeLabel && (
            <FormFieldSelect
              control={control as Control<StatusPopoverFormValues>}
              name="type"
              label={typeLabel}
              floatingLabel
              options={typeOptions}
              disabled={isPending}
              data-testid={`${testIdPrefix}-type-select`}
            />
          )}

          <Button
            type="submit"
            size="sm"
            disabled={isPending}
            data-testid={`${testIdPrefix}-submit-button`}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {addLabel}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/** Local Controller-backed color swatch — mirrors `BoardStatusFormDialog`'s color field. */
function ColorSwatchField({
  control,
  testIdPrefix,
  isPending,
}: {
  control: Control<NameColorFormValues>;
  testIdPrefix: string;
  isPending: boolean;
}) {
  const { field, fieldState } = useController({ control, name: 'color' });
  const colorValue = field.value ? getPaletteColor(field.value)?.value : undefined;

  return (
    <Field data-invalid={fieldState.invalid}>
      <FieldLabel className="sr-only">color</FieldLabel>
      <ColorPickerPopover
        value={field.value}
        onSelect={(color) => field.onChange(color)}
        trigger={
          <Button
            type="button"
            variant="outline"
            className="h-10 w-10 shrink-0 p-0"
            disabled={isPending}
            data-testid={`${testIdPrefix}-color-input`}
          >
            <div
              className="h-6 w-6 rounded-full border border-border"
              style={colorValue ? { background: colorValue } : undefined}
            />
          </Button>
        }
      />
      {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
    </Field>
  );
}
